/**
 * Month-and-year dates (e.g. the business start date).
 *
 * The picker only asks for a month and a year, but the field is stored as a full `YYYY-MM-DD`
 * date on the first of that month, because newform (CMI\Helpers) and lms both validate it with
 * `date_format:Y-m-d` and lms derives `months_in_business` from it. Keeping the stored format
 * unchanged means no backend change and old full dates (prepop, older leads) still display.
 */

/**
 * `'YYYY-MM-DD'` → `{ month, year }` as vue-datepicker's month-picker expects (month is 0-11).
 * Returns null for anything that is not a usable date string.
 */
export function monthValueFromDate(ymd) {
    if (typeof ymd !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
        return null;
    }
    const year = Number(ymd.slice(0, 4));
    const month = Number(ymd.slice(5, 7));
    if (month < 1 || month > 12) {
        return null;
    }
    return { month: month - 1, year };
}

/**
 * Month-picker value `{ month, year }` → `'YYYY-MM-01'`. Returns null when nothing was picked.
 */
export function firstOfMonthFromValue(value) {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const month = Number(value.month);
    const year = Number(value.year);
    if (!Number.isInteger(month) || !Number.isInteger(year) || month < 0 || month > 11) {
        return null;
    }
    return `${year}-${String(month + 1).padStart(2, '0')}-01`;
}

/**
 * Display text for the picked month, localised ("April 2025" / "avril 2025").
 */
export function formatMonthYear(date, lang = 'en') {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    const locale = lang === 'fr' ? 'fr-CA' : lang === 'es' ? 'es' : 'en-CA';
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}
